import React, { useState, useRef } from 'react';
import { showActivity, hideActivity } from '../activity';
import { API } from '../config';


function Converter() {
  const [mode, setMode] = useState('image');
  const [files, setFiles] = useState([]);
  const [quality, setQuality] = useState('high');
  const [converting, setConverting] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const IMAGE_ACCEPT = '.heic,.heif,.png,.webp,.tiff,.tif,.bmp,.avif';
  const VIDEO_ACCEPT = '.mp4,.mov,.webm,.avi,.mkv';

  const handleFileSelect = (selectedFiles) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    setFiles(Array.from(selectedFiles));
    setError('');
    setStatus('');
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  };

  const handleConvert = async () => {
    if (files.length === 0) return;
    setConverting(true);
    setError('');
    setStatus(mode === 'image' ? 'Converting images...' : 'Converting videos to GIF...');
    showActivity(mode === 'image' ? 'Converting images...' : 'Converting to GIF...');

    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    formData.append('quality', quality);

    const endpoint = mode === 'image' ? '/api/converter/image-to-jpg' : '/api/converter/video-to-gif';

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}${endpoint}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Conversion failed');
      }

      const contentType = res.headers.get('content-type');
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;

      if (contentType.includes('zip')) {
        a.download = mode === 'image'
          ? `converted_${files.length}_images.zip`
          : `converted_${files.length}_gifs.zip`;
      } else if (contentType.includes('gif')) {
        a.download = files[0].name.replace(/\.[^.]+$/, '') + '.gif';
      } else {
        a.download = files[0].name.replace(/\.[^.]+$/, '') + '.jpg';
      }

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
      setStatus(`✓ Converted ${files.length} file${files.length > 1 ? 's' : ''}`);
    } catch (err) {
      setError(err.message);
    }
    hideActivity();
    setConverting(false);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
    return (bytes / 1024 / 1024).toFixed(1) + ' MB';
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Converter</h1>
          <p>Convert image and video formats for Reddit posting.</p>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '600px' }}>

        {/* Mode selector */}
        <div className="card">
          <h2>Conversion Type</h2>
          <div style={{ display: 'flex', gap: '8px' }}>
            {[
              { id: 'image', label: '🖼️ Image → JPG', desc: 'HEIC, PNG, WEBP, TIFF, BMP, AVIF' },
              { id: 'video', label: '🎬 Video → GIF', desc: 'MP4, MOV, WEBM, AVI (max 30s)' }
            ].map(m => (
              <button
                key={m.id}
                onClick={() => { setMode(m.id); setFiles([]); setStatus(''); setError(''); setQuality(m.id === 'image' ? 'high' : 'medium'); }}
                style={{
                  flex: 1, padding: '12px', borderRadius: '8px', border: 'none',
                  cursor: 'pointer', fontWeight: '600', fontSize: '13px', textAlign: 'left',
                  background: mode === m.id ? '#ff4500' : '#2a2a2a',
                  color: mode === m.id ? '#ffffff' : '#aaaaaa',
                }}
              >
                <div>{m.label}</div>
                <div style={{ fontSize: '11px', fontWeight: '400', marginTop: '4px', opacity: 0.8 }}>{m.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Upload */}
        <div className="card">
          <h2>Files</h2>
          <div
            onDrop={handleDrop}
            onDragOver={e => e.preventDefault()}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: '2px dashed #333', borderRadius: '10px', padding: '24px',
              textAlign: 'center', cursor: 'pointer', color: '#555',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#ff4500'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#333'; }}
          >
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>{mode === 'image' ? '🖼️' : '🎬'}</div>
            <div style={{ fontSize: '14px' }}>Drop files here or click to upload</div>
            <div style={{ fontSize: '12px', marginTop: '4px' }}>
              {mode === 'image' ? 'HEIC, PNG, WEBP, TIFF, BMP, AVIF' : 'MP4, MOV, WEBM, AVI — max 30 seconds'}
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept={mode === 'image' ? IMAGE_ACCEPT : VIDEO_ACCEPT}
            multiple
            style={{ display: 'none' }}
            onChange={e => { if (e.target.files.length > 0) handleFileSelect(e.target.files); }}
          />
          {files.length > 0 && (
            <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {files.map((f, idx) => (
                <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#0f0f0f', borderRadius: '8px', padding: '8px 12px' }}>
                  <div>
                    <div style={{ fontSize: '13px', color: '#ffffff' }}>{f.name}</div>
                    <div style={{ fontSize: '11px', color: '#555' }}>{formatSize(f.size)}</div>
                  </div>
                  <button onClick={() => removeFile(idx)} style={{ background: 'transparent', border: 'none', color: '#ff4500', cursor: 'pointer', fontSize: '16px' }}>✕</button>
                </div>
              ))}
              <div style={{ fontSize: '11px', color: '#555', textAlign: 'right' }}>
                {files.length} file{files.length > 1 ? 's' : ''} selected
              </div>
            </div>
          )}
        </div>

        {/* Quality */}
        <div className="card">
          <h2>Quality</h2>
          <div style={{ display: 'flex', gap: '8px' }}>
            {[
              { id: 'high', label: 'Highest', desc: mode === 'image' ? 'JPEG 95%' : '24 fps, original size' },
              { id: 'medium', label: 'Medium', desc: mode === 'image' ? 'JPEG 80%' : '15 fps, 480px wide' },
              { id: 'low', label: 'Low', desc: mode === 'image' ? 'JPEG 60%' : '10 fps, 320px wide' }
            ].map(q => (
              <button
                key={q.id}
                onClick={() => setQuality(q.id)}
                style={{
                  flex: 1, padding: '10px', borderRadius: '8px', border: 'none',
                  cursor: 'pointer', fontWeight: '600', fontSize: '13px',
                  background: quality === q.id ? '#ff4500' : '#2a2a2a',
                  color: quality === q.id ? '#ffffff' : '#aaaaaa',
                }}
              >
                <div>{q.label}</div>
                <div style={{ fontSize: '10px', fontWeight: '400', marginTop: '2px', opacity: 0.7 }}>{q.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Convert button */}
        <div className="card">
          {status && (
            <div style={{ fontSize: '13px', color: status.startsWith('✓') ? '#22c55e' : '#aaaaaa', marginBottom: '12px' }}>
              {status}
            </div>
          )}
          {error && (
            <div style={{ fontSize: '13px', color: '#ef4444', marginBottom: '12px' }}>
              ✗ Error: {error}
            </div>
          )}
          <button
            onClick={handleConvert}
            disabled={files.length === 0 || converting}
            style={{
              width: '100%',
              opacity: files.length > 0 && !converting ? 1 : 0.5,
              cursor: converting ? 'wait' : 'pointer'
            }}
          >
            {converting
              ? `⏳ Converting ${files.length} file${files.length > 1 ? 's' : ''}...`
              : `🔄 Convert ${files.length > 0 ? files.length : ''} ${mode === 'image' ? 'to JPG' : 'to GIF'}`}
          </button>
          {converting && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ background: '#2a2a2a', borderRadius: '8px', height: '8px', overflow: 'hidden' }}>
                <div style={{
                  background: '#ff4500', height: '100%', borderRadius: '8px',
                  animation: 'progressPulse 2s ease-in-out infinite',
                  width: '60%'
                }} />
              </div>
              <div style={{ fontSize: '11px', color: '#555', marginTop: '6px' }}>
                {mode === 'video' ? 'Video to GIF can take 1-3 minutes for longer clips. Please wait...' : 'Converting images...'}
              </div>
              <style>{`
                @keyframes progressPulse {
                  0% { width: 20%; opacity: 0.6; }
                  50% { width: 80%; opacity: 1; }
                  100% { width: 20%; opacity: 0.6; }
                }
              `}</style>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Converter;